// Lịch sử biến động tồn của MỘT sản phẩm (mọi variant): nhập / bán / chuyển
// kho / kiểm kho — mở khi bấm vào số Tồn ở danh sách Sản phẩm.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListOrdered, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { apiGet } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { formatDateTime } from "@/lib/format/date";
import { ProductSerialsModal } from "./product-serials-modal";

interface HistoryRow {
  id: string;
  createdAt: string;
  type: string;
  quantityDelta: number;
  sku: string;
  warehouseName: string;
  note?: string | null;
  refType?: string | null;
  refId?: string | null;
  refCode?: string | null;
  performedBy?: string | null;
}

// Nhãn + màu theo loại giao dịch kho.
const TYPE_LABEL: Record<string, { label: string; cls: string }> = {
  import: { label: "Nhập hàng", cls: "bg-green-100 text-green-700" },
  sale: { label: "Bán ra", cls: "bg-blue-100 text-blue-700" },
  return: { label: "Trả / hoàn", cls: "bg-amber-100 text-amber-700" },
  transfer_in: { label: "Chuyển đến", cls: "bg-violet-100 text-violet-700" },
  transfer_out: { label: "Chuyển đi", cls: "bg-violet-100 text-violet-700" },
  adjust: { label: "Điều chỉnh", cls: "bg-orange-100 text-orange-700" },
  stocktake: { label: "Kiểm kho", cls: "bg-orange-100 text-orange-700" },
  rma_out: { label: "Xuất bảo hành", cls: "bg-rose-100 text-rose-700" },
};

function typeBadge(t: string) {
  const m = TYPE_LABEL[t] ?? { label: t, cls: "bg-muted text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

// Mã phiếu/đơn bấm được khi resolve được — route chi tiết cần UUID (refId),
// còn refCode chỉ là nhãn hiển thị (HD26… / IM26…).
function refLink(row: HistoryRow, onClose: () => void) {
  if (!row.refCode) return <span className="text-muted-foreground">—</span>;
  if (row.refType === "order" && row.refId) {
    return (
      <Link
        to="/orders/$id"
        params={{ id: row.refId }}
        className="text-primary hover:underline font-mono text-xs"
        onClick={onClose}
      >
        {row.refCode}
      </Link>
    );
  }
  if (row.refType === "import" && row.refId) {
    return (
      <Link
        to="/inventory/imports/$id"
        params={{ id: row.refId }}
        className="text-primary hover:underline font-mono text-xs"
        onClick={onClose}
      >
        {row.refCode}
      </Link>
    );
  }
  return <span className="font-mono text-xs">{row.refCode}</span>;
}

interface Props {
  productId: string;
  productName: string;
  onClose: () => void;
}

export function ProductStockHistoryModal({ productId, productName, onClose }: Props) {
  // Danh sách IMEI của sản phẩm — mở chồng lên modal này (z-index cao hơn).
  const [serialsOpen, setSerialsOpen] = useState(false);
  const { data: rows, isLoading } = useQuery({
    queryKey: ["product-stock-history", productId],
    queryFn: () => apiGet<HistoryRow[]>(`/v1/products/${productId}/stock-history`, { limit: 200 }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <p className="font-semibold">Lịch sử xuất nhập kho</p>
            <p className="truncate text-sm text-muted-foreground">{productName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSerialsOpen(true)} className="gap-1.5">
              <ListOrdered size={15} />
              Danh sách IMEI
            </Button>
            <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted" aria-label="Đóng">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-auto p-4">
          {isLoading ? (
            <p className="text-muted-foreground">Đang tải…</p>
          ) : !rows?.length ? (
            <p className="text-muted-foreground">Chưa có biến động tồn kho nào.</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>Thời gian</Th>
                  <Th>Loại</Th>
                  <Th className="text-right">SL</Th>
                  <Th>Kho</Th>
                  <Th>SKU</Th>
                  <Th>Mã phiếu/đơn</Th>
                  <Th>Người thực hiện</Th>
                  <Th>Ghi chú</Th>
                </Tr>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <Tr key={r.id}>
                    <Td className="whitespace-nowrap text-xs">{formatDateTime(r.createdAt)}</Td>
                    <Td>{typeBadge(r.type)}</Td>
                    <Td
                      className={`text-right font-mono font-medium ${
                        r.quantityDelta > 0 ? "text-green-600" : r.quantityDelta < 0 ? "text-red-600" : ""
                      }`}
                    >
                      {r.quantityDelta > 0 ? `+${r.quantityDelta}` : r.quantityDelta}
                    </Td>
                    <Td className="whitespace-nowrap">{r.warehouseName}</Td>
                    <Td className="font-mono text-xs">{r.sku}</Td>
                    <Td>{refLink(r, onClose)}</Td>
                    <Td className="text-xs text-muted-foreground">{r.performedBy ?? "—"}</Td>
                    <Td className="max-w-[220px] truncate text-xs text-muted-foreground" title={r.note ?? ""}>
                      {r.note || "—"}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </div>

      {serialsOpen && (
        <ProductSerialsModal
          productId={productId}
          productName={productName}
          onClose={() => setSerialsOpen(false)}
        />
      )}
    </div>
  );
}
